<template>
    <div>
        <table
            class="min-w-full border border-gray-300 divide-y divide-gray-300 dark:border-neutral-800 dark:divide-neutral-800"
        >
            <thead class="bg-gray-50 dark:bg-neutral-800">
                <tr>
                    <th
                        scope="col"
                        class="py-3.5 px-4 text-left text-xs font-semibold text-gray-900 dark:text-neutral-50"
                    >
                        {{ lang('products::configurations.fields.name') }}
                    </th>
                    <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-xs font-semibold text-gray-900 dark:text-neutral-50"
                    >
                        {{ lang('products::configurations.fields.settings') }}
                    </th>
                    <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-6"></th>
                </tr>
            </thead>
            <tbody v-if="modelValue && modelValue.length > 0" class="divide-y divide-gray-200 dark:divide-neutral-800">
                <tr v-for="(configuration, index) in modelValue" :key="index">
                    <th
                        scope="row"
                        class="w-[20%] py-3.5 px-4 text-left text-sm font-semibold text-gray-900 bg-gray-50 dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 align-top"
                    >
                        <AdminText v-model="configuration.name"></AdminText>
                    </th>
                    <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 align-top whitespace-nowrap">
                        <table class="w-full">
                            <tbody>
                                <tr>
                                    <th scope="row" class="py-1 pr-2 text-right" width="130">
                                        {{ lang('products::configurations.fields.type') }}
                                    </th>
                                    <td>
                                        <AdminSelect v-model="configuration.type" :options="types"></AdminSelect>
                                    </td>
                                </tr>
                                <tr>
                                    <th scope="row" class="py-1 pr-2 text-right">
                                        {{ lang('products::configurations.fields.description') }}
                                    </th>
                                    <td>
                                        <AdminTextarea v-model="configuration.description" />
                                    </td>
                                </tr>
                                <tr>
                                    <th scope="row" class="py-1 pr-2 text-right">
                                        {{ lang('products::configurations.fields.media_id') }}
                                    </th>
                                    <td>
                                        <AdminFile
                                            v-model="configuration.media"
                                            :accept="'image/*'"
                                            :recommended="{
                                                width: 1280,
                                                height: 720,
                                                aspect: '16:9',
                                                sizes: ['750×422', '1280×720'],
                                            }"
                                        ></AdminFile>
                                    </td>
                                </tr>
                                <tr>
                                    <th scope="row" class="py-1 pr-2 text-right">
                                        {{ lang('products::configurations.fields.is_optional') }}
                                    </th>
                                    <td>
                                        <AdminCheckbox
                                            v-model="configuration.is_optional"
                                            :checked="configuration.is_optional === 1"
                                        ></AdminCheckbox>
                                    </td>
                                </tr>
                                <tr v-if="configuration.type === 'text'">
                                    <th scope="row" class="py-1 pr-2 text-right">
                                        {{ lang('products::configurations.fields.default_value') }}
                                    </th>
                                    <td>
                                        <AdminSuggestions
                                            v-model="configuration.default_value"
                                            ajax-route="products/configurations.admin.api.suggestions"
                                            :searchable="true"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </td>
                    <td class="w-12 py-3.5 pr-4 sm:pr-6 align-top">
                        <Button @click.prevent="removeConfiguration(configuration)">
                            <span class="flex items-center justify-center size-8">
                                <icon icon="hero/outline/trash" class="size-4"></icon>
                            </span>
                        </Button>
                    </td>
                </tr>
            </tbody>
            <tbody v-else>
                <tr>
                    <td
                        colspan="3"
                        class="py-3.5 space-y-1 text-center text-sm font-semibold text-neutral-900 dark:text-neutral-100"
                    >
                        <p>
                            {{ lang('products::configurations.messages.no_values') }}
                        </p>
                        <p>
                            <Button @click.prevent="addConfiguration">
                                <span>
                                    {{ lang('products::configurations.actions.add_configuration') }}
                                </span>
                                <icon icon="hero/outline/plus" class="size-4"></icon>
                            </Button>
                        </p>
                    </td>
                </tr>
            </tbody>

            <tfoot class="bg-gray-50 dark:bg-neutral-900">
                <tr>
                    <th
                        colspan="2"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-neutral-900 dark:text-neutral-100 sm:pl-6"
                    ></th>
                    <th class="w-12 py-3.5 pr-4 sm:pr-6">
                        <Button @click.prevent="addConfiguration">
                            <span class="flex items-center justify-center size-8">
                                <icon icon="hero/outline/plus" class="size-4"></icon>
                            </span>
                        </Button>
                    </th>
                </tr>
            </tfoot>
        </table>

        <MountedTeleport to="#aside">
            <Sortable :list="modelValue" item-key="id" @end="sorted">
                <template #item="{ element }">
                    <div
                        :key="element.id"
                        class="flex items-stretch text-xs border select-none bg-gray-50 hover:bg-blue-100 dark:bg-neutral-900 dark:hover:bg-blue-950"
                    >
                        <a :href="`#content-${element.id}`" class="flex items-center justify-center grow-0 w-6">
                            <icon icon="hero/outline/chevron-left" class="size-4"></icon>
                        </a>
                        <div class="flex-auto px-4 py-2">
                            <a :href="`#content-${element.id}`">
                                {{ element.name }}
                            </a>
                        </div>
                        <div
                            class="flex items-center grow-0 w-6 text-center bg-gray-100 border dark:border-neutral-800 dark:bg-neutral-900 cursor-grab"
                        >
                            <icon icon="hero/outline/dots-vertical" class="mx-auto pointer-events-none size-4"></icon>
                        </div>
                    </div>
                </template>
            </Sortable>
        </MountedTeleport>
    </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { lang } from '@/lang/composable';
import { arrayMove } from '@src/Common/Helpers/array';
import type { FieldComponent } from '@src/Common/Components/types';

const modelValue = defineModel({
    type: [Object, undefined],
    default: () => [],
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const props = withDefaults(defineProps<FieldComponent>(), {});

const addConfiguration = () => {
    modelValue.value = [
        ...modelValue.value,
        {
            id: null,
            name: '',
            type: 'text',
            description: '',
            media: null,
            default_value: null,
            is_optional: false,
        },
    ];
};

const removeConfiguration = (configuration) => {
    modelValue.value = modelValue.value.filter((item) => item !== configuration);
};

const types = ref([
    { id: 'text', name: lang('products::configurations.fields.types.text') },
    { id: 'file', name: lang('products::configurations.fields.types.file') },
]);

const sorted = ({ newIndex, oldIndex }) => {
    modelValue.value = arrayMove(modelValue.value, oldIndex, newIndex);
};
</script>
